import React from 'react'
import classNames from 'classnames'
import { Media as MediaType } from '@/payload-types'
import { CMSImage } from '@/ui/atoms/CMSImage'
import { cva, type VariantProps } from 'class-variance-authority'

type AvatarProps = {
  className?: string
  showSkeleton?: boolean
  variant?: 'rounded' | 'circle'
  size?: 'mini' | 'medium' | 'big'
  userImage?: MediaType | string | null
  href?: string
  initials?: string
  showHoverEffect?: boolean
} & VariantProps<typeof avatarVariants>

const avatarVariants = cva(
  'overflow-hidden border-[1px] border-neutral-95 bg-base outline outline-[1px] outline-neutral-95',
  {
    defaultVariants: {
      size: 'medium',
      variant: 'rounded',
    },
    variants: {
      size: {
        mini: 'min-w-6 !size-6',
        medium: 'min-w-10 !size-10',
        big: 'min-w-16 !size-16',
      },
      variant: {
        rounded: 'rounded-md',
        circle: 'rounded-full',
      },
    },
  },
)

const SkeletonLoader: React.FC = () => (
  <div className="animate-pulse bg-soft-gradient-blue h-full w-full"></div>
)

export const Avatar: React.FC<AvatarProps> = ({
  className: classNameFromProps,
  showSkeleton,
  userImage,
  size,
  variant,
  initials,
  showHoverEffect = false,
}) => {
  const userComponent = showSkeleton ? (
    <SkeletonLoader />
  ) : userImage ? (
    <CMSImage
      height={64}
      width={64}
      resource={userImage}
      className={`object-cover w-full h-full`}
    />
  ) : (
    <div
      className={
        'uppercase flex justify-center items-center text-label-S-regular h-full w-full bg-neutral-40 text-text-inverted'
      }
    >
      {initials?.substring(0, 2)}
    </div>
  )
  return (
    <div
      className={classNames(
        classNameFromProps,
        { 'hover:outline-border-focus transition-all': showHoverEffect },
        avatarVariants({ size, variant }),
      )}
    >
      {userComponent}
    </div>
  )
}

export default Avatar
